import React from "react";
import PropTypes from "prop-types";
//Material imports
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import MuiDialogTitle from "@mui/material/DialogTitle";
import makeStyles from "@mui/styles/makeStyles";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";

//Limitless imports
import { useTranslation } from "@limitless/common/hooks";
import { PortalButton } from "@limitless/portal";
const useStyles = makeStyles(theme => ({
  dialog: {
    width: 600,
    borderRadius: 20,
    minHeight: 150
  },
  loadingContainer: {
    display: "flex",
    flex: 1,
    justifyContent: "center",
    alignItems: "center"
  },
  tabsFlexContainer: {
    justifyContent: "center",
    backgroundColor: "#34467a"
  },
  overlayButton: {
    width: "200px",
    margin: "30px auto",
    textTransform: "none",
    backgroundColor: theme.palette.primary.dark,
    fontSize: ".7rem",
    "&:hover": {
      backgroundColor: theme.palette.primary.light
    }
  },
  actionCenter: {
    marginLeft: 0,
    justifyContent: "center"
  },
  contentStyle: {
    marginTop: 10,
    textAlign: "center"
  },
  closeButton: {
    position: "absolute",
    right: 10,
    marginBottom: 20,
    color: theme.palette.background.dark
  },
  bannedStateContainer: {
    ...theme.grid.gridTemplateContainer,
    minWidth: 300,
    flexDirection: "column",
    alignContent: "center",
    gridTemplateAreas: `"smiley"
                            "header"
                            "text1"
                            "text2"
                            "text3"`
  },
  smiley: {
    gridArea: "smiley",
    margin: "auto",
    width: "20%",
    marginBottom: 20
  },
  header: {
    marginBottom: 10,
    fontWeight: 700,
    gridArea: "header"
  },
  text1: {
    gridArea: "text1",
    marginBottom: 40
  },
  text2: {
    gridArea: "text2"
  },
  text3: {
    gridArea: "text3"
  }
}));
const DialogTitle = ({
  onClose,
  showCloseButton
}) => {
  const classes = useStyles();
  return /*#__PURE__*/React.createElement(MuiDialogTitle, {
    style: {
      marginBottom: 10
    }
  }, onClose && showCloseButton ? /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": "close",
    className: classes.closeButton,
    onClick: onClose,
    size: "large"
  }, /*#__PURE__*/React.createElement(CloseIcon, null)) : null);
};
const INITIAL_STATE = {
  btnTitle: null,
  contentData: null,
  showCloseButton: true
};
const renderBannedState = (classes, t) => {
  return /*#__PURE__*/React.createElement(Grid, {
    container: true,
    className: classes.bannedStateContainer
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.smiley,
    component: "img",
    src: "/error_smiley.png"
  }), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.header,
    component: Typography,
    variant: "h3",
    color: "textPrimary"
  }, t("We're sorry,")), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.text1,
    component: Typography,
    variant: "body1",
    color: "textPrimary"
  }, t("it looks like we cannot sign you up at this time")), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    className: classes.text2,
    component: Typography,
    variant: "body2",
    color: "textPrimary"
  }, t("Unfortunately this scheme is not available in your area.")));
};
const Overlay = ({
  open,
  onClose,
  content
}) => {
  var _content$userData6;
  const {
    t
  } = useTranslation();
  const classes = useStyles();
  const [overlayState, setOverlayState] = React.useState({
    ...INITIAL_STATE,
    onBtnClick: onClose
  });
  React.useEffect(() => {
    var _content$userData, _content$userData2;
    if (((_content$userData = content.userData) === null || _content$userData === void 0 ? void 0 : _content$userData.error) === "BannedState") {
      setOverlayState({
        showCloseButton: false,
        btnTitle: null,
        contentData: renderBannedState(classes, t)
      });
    } else if (content !== null && content !== void 0 && (_content$userData2 = content.userData) !== null && _content$userData2 !== void 0 && _content$userData2.groupHash) {
      var _content$userData3, _content$userData4;
      if (((_content$userData3 = content.userData) === null || _content$userData3 === void 0 ? void 0 : _content$userData3.error) === "UsernameExistsException") {
        setOverlayState({
          onBtnClick: () => window.open(process.env.REACT_APP_PORTAL_URL, "_blank"),
          btnTitle: t("Sign In"),
          contentData: t("The email address already exists in our system. Please Sign In instead")
        });
      } else if (((_content$userData4 = content.userData) === null || _content$userData4 === void 0 ? void 0 : _content$userData4.error) !== "UsernameExistsException") {
        var _content$userData5;
        setOverlayState({
          ...INITIAL_STATE,
          btnTitle: t("Close"),
          contentData: (_content$userData5 = content.userData) === null || _content$userData5 === void 0 ? void 0 : _content$userData5.error
        });
      } else {
        setOverlayState({
          ...INITIAL_STATE,
          btnTitle: t("Close"),
          contentData: t("Try again later")
        });
      }
    } else {
      setOverlayState({
        ...INITIAL_STATE,
        btnTitle: t("Close"),
        contentData: t("Not implemented")
      });
    }
  }, [content === null || content === void 0 ? void 0 : (_content$userData6 = content.userData) === null || _content$userData6 === void 0 ? void 0 : _content$userData6.error]);
  return /*#__PURE__*/React.createElement(Dialog, {
    open: open,
    onClose: onClose,
    classes: {
      root: classes.root,
      paper: classes.dialog
    },
    disablePortal: false,
    maxWidth: "lg",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(DialogTitle, {
    id: "overlay-title",
    onClose: onClose,
    showCloseButton: Overlay.showCloseButton
  }), /*#__PURE__*/React.createElement(DialogContent, {
    classes: {
      root: classes.contentStyle
    }
  }, overlayState.contentData), overlayState.btnTitle && /*#__PURE__*/React.createElement(DialogActions, {
    classes: {
      root: classes.actionCenter
    }
  }, /*#__PURE__*/React.createElement(PortalButton, {
    buttonClasses: classes.overlayButton,
    onClick: overlayState.onBtnClick,
    disabled: false,
    id: "overlayButton"
  }, t(overlayState.btnTitle))));
};
export default Overlay;
Overlay.propTypes = {
  open: PropTypes.bool.isRequired,
  content: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired
};
DialogTitle.propTypes = {
  onClose: PropTypes.func.isRequired,
  showCloseButton: PropTypes.bool
};